import React, { useContext, useEffect, useMemo, useState } from 'react'
import MediaQuery from 'react-responsive'
import { useLocation } from 'react-router'

import { AcornsIcon } from '@acorns/icons'
import { ActionStrip, ConfirmationModal, ModalActionTypes, Spinner } from '@acorns/web-components'
import { StyledRemoteString } from '@acorns/web-utils'
import { useMutation, useQuery } from '@apollo/react-hooks'
import { useFeature } from '@optimizely/react-sdk'
import { compose } from 'recompose'

import { MOC_ONBOARDING_FLOW } from 'app-constants'
import AlertBadge from 'assets/icons/alert-badge.png'
import BitcoinIcon from 'assets/icons/bitcoin-icon.svg'
import NewMark from 'assets/icons/new-mark.svg'
import Button from 'components/Button'
import ETFDoughnut from 'components/charts/ETFDoughnut'
import ETFList from 'components/charts/ETFList'
import { ErrorContainer } from 'components/ErrorContainer'
import Layout from 'components/Layout'
import Nav from 'components/Nav'
import Separator from 'components/Separator'
import { withClickTrack } from 'components/Trackers/ClickTracker'
import { TRACKER_IDS } from 'components/Trackers/trackerIDs'
import {
  ActionType,
  FunnelName,
  InvestmentAccount,
  InvestmentProduct,
  LinkedAccount,
  PageName,
  PortfolioSecurityAllocation,
  PortfolioTheme,
  Portfolio as PortfolioType,
  RoundUpProfile,
  Suitability_Portfolio,
  User,
} from 'generated/types'
import { TestIDs } from 'test/utils/testIds'
import themeValues from 'theme'
import trackEvent from 'utils/analytics/trackEvent'
import { WithFlowRouterProps, withFlowRouter } from 'utils/FlowProvider'
import { FlowRoute } from 'utils/FlowProvider/types'
import { checkIsEligibleRoundUps } from 'utils/FlowProvider/utils'
import { FunnelContext } from 'utils/FunnelContext'
import LinkedAccountsQuery from 'utils/mutators/bank-linking/LinkedAccounts.query.gql'
import { formatPercentage } from 'utils/number/percentage'
import { WithOptimizelyProps, withOptimizely } from 'utils/Optimizely'
import { bitcoinInRegistrationExperiment } from 'utils/Optimizely/features'
import { useRemoteStrings } from 'utils/RemoteString'

import AllPortfolios from '../allPortfolios.gql'
import {
  AboutYourPortfolioButtonTapped,
  PreviewPortfolioOpenAccountTapped,
  PreviewPortfolioScreenViewed,
} from '../events'
import registrationCompleteMutation from '../registrationComplete.gql'
import { PortfolioThemes } from '../select-portfolio'
import UpdateAccountPortfolio from '../updateAccountPortfolioMutation.gql'
import UserInvestmentDetails from '../userInvestmentDetails.gql'
import AboutYourPortfolio from './AboutYourPortfolio'
import { useCrypto } from './Crypto'
import {
  BitcoinETFSection,
  CustomMobileContainer,
  Footer,
  Header,
  HeadingStyled,
  NEWSection,
  StyledBitcoinButton,
  StyledBitcoinImage,
  StyledHeaderBodyText,
  StyledNewImage,
  ThickSeparator,
} from './styled-components'
import SustainabilityInfo from './SustainabilityInfo'
import YearProjectionOverview from './YearProjectionOverview'

type ChangeThemeMutation = {
  investmentAccountId: string
  theme: string
  riskLevel: string
}

type AllPortfolioDataQuery = {
  portfolios: PortfolioType[]
  recommendedPortfolio: Suitability_Portfolio
}

type UserDetailsDataQuery = {
  user: User
  investmentAccountsByUserId: InvestmentAccount[]
  roundUpProfile: RoundUpProfile
}

type LinkedAccountsDataQuery = {
  linkedAccounts: LinkedAccount[]
}

type Props = WithFlowRouterProps & WithOptimizelyProps

const OpenAccountButton = withClickTrack<any, any>(TRACKER_IDS.PORTFOLIO_OVERVIEW.SUBMIT_BUTTON)(
  Button,
)

const enhance = compose(withFlowRouter, withOptimizely)

const PreviewPortfolio: React.FC<Props> = ({
  flow,
  route,
  trackOptimizely,
  getVariation,
  getOptimizelyExperimentConfig,
}) => {
  const location = useLocation()
  const LATERFLOW = 'laterOnboarding'
  const isBitcoinFlow = location?.state?.isBitcoinFlow
  const { isBackEnabled, isDynamicFunnelEnabled, routeToPage, metadata } = useContext(FunnelContext)
  const isMightyOakCard = isDynamicFunnelEnabled
    ? metadata.name === FunnelName.MightyOakCard
    : flow === MOC_ONBOARDING_FLOW
  const { getRemoteString } = useRemoteStrings()
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [selectedEtf, setSelectedEtf] = useState(null)
  const [isGeneralErrorModalOpen, setIsGeneralErrorModalOpen] = useState(false)
  const [changeTheme] = useMutation<ChangeThemeMutation>(UpdateAccountPortfolio)
  const [registrationComplete] = useMutation(registrationCompleteMutation)
  const [isbitcoinCTAEnabled] = useFeature('bitcoin_in_registration')
  const { data: userDetailsData, loading: isLoadingUserDetails } = useQuery<UserDetailsDataQuery>(
    UserInvestmentDetails,
    {
      fetchPolicy: 'network-only',
    },
  )

  const { data: allPortfoliosData, loading: isLoadingPortfolioData } = useQuery<
    AllPortfolioDataQuery
  >(AllPortfolios, {
    fetchPolicy: 'network-only',
    variables: {
      securitySymbol: isBitcoinFlow && 'BITO',
    },
  })

  const { data: linkedAccountsData, loading: isLoadingLinkedAccountData } = useQuery<
    LinkedAccountsDataQuery
  >(LinkedAccountsQuery, {
    fetchPolicy: 'network-only',
    skip: isMightyOakCard,
  })
  const isReferred = userDetailsData?.user?.sourceId === 'Referrals'
  const { cryptoEtf, isCryptoDataLoading, investmentAccount, addCryptoToPortfolio } = useCrypto()
  let recommendedRisk = allPortfoliosData?.recommendedPortfolio?.name
  let investmentAccountId = ''
  let investmentAccountType = ''
  let portfolioDescription = getRemoteString({
    key: `portfolio.recommended.about.description.${recommendedRisk
      ?.toLowerCase()
      .replace(' ', '_')}`,
  })
  const product = flow === LATERFLOW ? 'Later' : 'Invest'
  if (flow === LATERFLOW) {
    const laterAccount = userDetailsData?.investmentAccountsByUserId.find(
      account => account.product === InvestmentProduct.Later,
    )
    // keeps age based portfolio for later
    recommendedRisk = laterAccount?.portfolio?.name ?? ''
    investmentAccountId = laterAccount?.id
    investmentAccountType = laterAccount?.investmentAccountType
    portfolioDescription = getRemoteString({
      key: 'portfolio.recommended.about.description.later',
    })
  } else if (isBitcoinFlow) {
    investmentAccountId = investmentAccount.id
  } else {
    const investAccount = userDetailsData?.investmentAccountsByUserId.find(
      account => account.product === InvestmentProduct.Invest,
    )
    investmentAccountId = investAccount?.id
  }

  const fundingSource = userDetailsData?.user?.fundingSource?.type ?? false
  const roundUpsEnabled = userDetailsData?.roundUpProfile?.roundUpsEnabled ?? false
  const isEligibleRoundUps = checkIsEligibleRoundUps(linkedAccountsData?.linkedAccounts)
  const selectedTheme = localStorage.getItem('portfolioTheme') ?? PortfolioThemes.CORE
  const isConservative = recommendedRisk === 'Conservative'
  const isSustainable = selectedTheme === PortfolioThemes.SUSTAINABLE
  const optimizelyVariation = getVariation(bitcoinInRegistrationExperiment)

  let portfolioData = {} as PortfolioType

  if (selectedTheme === PortfolioThemes.CORE) {
    const filteredPortfolios = allPortfoliosData?.portfolios?.filter(
      (portfolio: PortfolioType) => portfolio?.theme === PortfolioTheme.Standard,
    )
    portfolioData = filteredPortfolios?.filter(
      (portfolio: PortfolioType) => portfolio.name === recommendedRisk,
    )[0]
  } else {
    const filteredPortfolios = allPortfoliosData?.portfolios?.filter(
      (portfolio: PortfolioType) => portfolio?.theme === PortfolioTheme.Sustainable,
    )
    portfolioData = filteredPortfolios?.filter(
      (portfolio: PortfolioType) => portfolio.name === recommendedRisk,
    )[0]
  }
  const formattedRisk = recommendedRisk?.toUpperCase().replace(/ /g, '_')

  const portfolioSecurityAllocations = portfolioData?.portfolioSecurityAllocations ?? []
  const graphData = useMemo(
    () =>
      portfolioSecurityAllocations.map(
        ({ allocationPercent }: PortfolioSecurityAllocation) => allocationPercent / 100,
      ),
    [portfolioSecurityAllocations],
  )

  const graphLabels = useMemo(
    () =>
      portfolioSecurityAllocations.map(
        ({ security }: PortfolioSecurityAllocation) => security?.displayName ?? '',
      ),
    [portfolioSecurityAllocations],
  )

  // calculate portfolioPercentage(Sum)
  let totalBondsPercentage = 0
  let totalStocksPercentage = 0
  let totalCryptoPercentage = 0

  portfolioSecurityAllocations.forEach(security => {
    if (security?.investmentCategories?.bond === 100) {
      totalBondsPercentage += security?.allocationPercent
    } else if (security?.investmentCategories?.crypto === 100) {
      totalCryptoPercentage = security.allocationPercent
    } else {
      totalStocksPercentage += security?.allocationPercent
    }
  })
  const bondEtfPercent = formatPercentage(totalBondsPercentage / 100)
  const stockEtfPercent = formatPercentage(totalStocksPercentage / 100)
  const cryptoEtfPercent = formatPercentage(totalCryptoPercentage / 100)
  const handleRouting = () => {
    if (isDynamicFunnelEnabled) {
      let action
      // Temporary workaround until we work in the logic for state management
      if (isMightyOakCard || !fundingSource) {
        action = ActionType.Continue
      } else {
        !isEligibleRoundUps ? (action = ActionType.Direct) : (action = ActionType.Skip)
      }
      routeToPage(PageName.PreviewPortfolio, action, { isReferred })
    } else {
      route({
        from: FlowRoute.PreviewPortfolio,
        action: isMightyOakCard && isReferred ? 'switch' : 'continue',
        ...(!isMightyOakCard && {
          state: {
            fundingSource,
            roundUpsEnabled,
            isEligibleRoundUps,
          },
        }),
      })
    }
  }
  const handleOpenAccount = async () => {
    setIsLoading(true)
    trackEvent('registrationPreviewPortfolioOpenAccountTapped', {
      eventData: PreviewPortfolioOpenAccountTapped(recommendedRisk),
    })

    /*
      Adding guard against null accountId per backend request. Calling the UpdateAccountPortfolio mutation with a null id is causing an error in backend.
      More information can be found here: https://acorns.atlassian.net/browse/IP-3723
    */
    if (!investmentAccountId) {
      setIsLoading(false)
      setIsGeneralErrorModalOpen(true)
      return
    }

    try {
      await changeTheme({
        variables: {
          investmentAccountId,
          theme:
            selectedTheme === PortfolioThemes.SUSTAINABLE
              ? PortfolioTheme.Sustainable
              : PortfolioTheme.Standard,
          riskLevel: formattedRisk,
        },
      })
    } catch (error) {
      setIsLoading(false)
    }

    await registrationComplete()
    handleRouting()
  }

  const handleLearnMoreESG = () => {
    // Dynamic funnels is not enabled here because this CTA belongs to this page and will
    // never change routing
    route({ from: FlowRoute.PreviewPortfolio, action: 'learn_more_esg' })
  }

  const handleAboutPortfolio = () => {
    trackEvent('Button Tapped', { eventData: AboutYourPortfolioButtonTapped(recommendedRisk) })

    const locationState = { product, selectedTheme, recommendedRisk }
    isDynamicFunnelEnabled
      ? routeToPage(PageName.PreviewPortfolio, ActionType.MoreInfo, locationState)
      : route({ from: FlowRoute.PreviewPortfolio, action: 'about_portfolio', locationState })
  }

  const handleAddBitcoinETF = () => {
    trackOptimizely('bitcoin_add_to_portfolio_pressed')
    route({
      from: FlowRoute.PreviewPortfolio,
      action: 'add_bitcoin_etf',
      locationState: { portfolioData, formattedRisk },
    })
  }

  const sendExperimentViewedEvent = variation => {
    const experimentConfig = getOptimizelyExperimentConfig(bitcoinInRegistrationExperiment)
    const variationConfig = experimentConfig?.variationsMap[variation]
    if (variationConfig) {
      return trackEvent('Experiment Viewed', {
        eventData: {
          object_name: 'optimizelyExperiment',
          experimentName: experimentConfig.key,
          experimentId: experimentConfig.id,
          variationId: variationConfig.id,
          variationName: variationConfig.key,
        },
      })
    }
  }
  const renderHeader = () => (
    <Header>
      <HeadingStyled data-testid={TestIDs.PREVIEW_PORTFOLIO.TITLE}>
        {getRemoteString({
          key: 'portfolio.recommended.top_title.variable',
          variables: [product],
        })}
      </HeadingStyled>
      <StyledHeaderBodyText data-testid={TestIDs.PREVIEW_PORTFOLIO.BODY}>
        {product === 'Later' ? (
          <StyledRemoteString
            stringKey="portfolio.recommended.top_description.later.v1.markdown"
            screen="registrationPreviewPortfolio"
          />
        ) : (
          <StyledRemoteString
            stringKey="portfolio.recommended.top_description.v1.markdown"
            screen="registrationPreviewPortfolio"
          />
        )}
      </StyledHeaderBodyText>
    </Header>
  )

  useEffect(() => {
    if (isBitcoinFlow) {
      addCryptoToPortfolio({
        variables: {
          input: {
            symbol: cryptoEtf.symbol,
            investmentAccountId: investmentAccount.id,
          },
        },
      })
    }
  }, [isBitcoinFlow, addCryptoToPortfolio, cryptoEtf, investmentAccount])

  useEffect(() => {
    if (recommendedRisk !== undefined) {
      trackEvent('registrationPreviewPortfolioViewed', {
        eventData: PreviewPortfolioScreenViewed(recommendedRisk),
      })
    }
  }, [recommendedRisk])

  useEffect(() => {
    trackOptimizely('bitcoin_preview_portfolio_screen_viewed')
  }, [])

  useEffect(() => {
    if (optimizelyVariation) {
      sendExperimentViewedEvent(optimizelyVariation)
    }
  }, [optimizelyVariation])

  const actionStripActions = [
    {
      id: 0,
      text: '',
      actionText: 'Go to about portfolio',
      handler: handleAboutPortfolio,
      icon: <AcornsIcon icon={AcornsIcon.Icon.ProductGrow} width="24px" height="24px" />,
    },
  ]
  const isLoadingData =
    isLoading ||
    isLoadingPortfolioData ||
    isLoadingUserDetails ||
    isLoadingLinkedAccountData ||
    isCryptoDataLoading

  if (!isLoadingData && !portfolioData) {
    return (
      <>
        <Nav
          back={isDynamicFunnelEnabled && isBackEnabled(PageName.PreviewPortfolio)}
          hideProgressBar={true}
        />
        <Layout>
          <ErrorContainer message={getRemoteString({ key: 'error.generic.body' })} />
        </Layout>
      </>
    )
  }

  return (
    <>
      {isLoadingData ? (
        <>
          <Nav
            back={isDynamicFunnelEnabled && isBackEnabled(PageName.PreviewPortfolio)}
            hideProgressBar={true}
          />
          <CustomMobileContainer>
            <Spinner />
          </CustomMobileContainer>
        </>
      ) : (
        <>
          <Nav
            back={isDynamicFunnelEnabled && isBackEnabled(PageName.PreviewPortfolio)}
            hideProgressBar={true}
            title={'Your ' + selectedTheme + ' Portfolio'}
          />
          <MediaQuery minWidth={themeValues.breakpoints.md + 1}>
            <ActionStrip
              maxWidth={800}
              title={'Review ' + product + ' Portfolio'}
              showBack={false}
              actions={actionStripActions}
            />
          </MediaQuery>
          <Layout fullWidth={true}>
            <ETFDoughnut
              bondEtfPercent={bondEtfPercent}
              cryptoEtfPercent={cryptoEtfPercent}
              graphData={graphData}
              hasDisplayName={true}
              header={renderHeader}
              portfolio={portfolioData}
              stockEtfPercent={stockEtfPercent}
              setSelectedEtf={setSelectedEtf}
              selectedEtf={selectedEtf}
              graphLabels={graphLabels}
            />
            {!isBitcoinFlow && isbitcoinCTAEnabled && (
              <>
                <NEWSection>
                  <StyledNewImage src={NewMark} alt="New Mark" />
                </NEWSection>
                <BitcoinETFSection>
                  <StyledBitcoinButton onPress={handleAddBitcoinETF}>
                    <StyledBitcoinImage src={BitcoinIcon} alt="Bitcoin" />
                    Add a Bitcoin ETF
                  </StyledBitcoinButton>
                </BitcoinETFSection>
              </>
            )}
            <AboutYourPortfolio
              portfolioRisk={recommendedRisk}
              portfolioTheme={selectedTheme}
              portfolioDescription={portfolioDescription}
              product={product}
              accountType={investmentAccountType}
            />
            <ThickSeparator />
            <ETFList etfs={portfolioSecurityAllocations} selectedIndex={selectedEtf} />
            {!isConservative && (
              <>
                <ThickSeparator />
                <YearProjectionOverview
                  lowProjection={portfolioData?.projection.lowestGrowthRate}
                  highProjection={portfolioData?.projection.highestGrowthRate}
                  averageProjection={portfolioData?.projection.averageGrowthRate}
                />
              </>
            )}
            {isSustainable && (
              <>
                <ThickSeparator />
                <SustainabilityInfo handleSustainableLinkPress={handleLearnMoreESG} />
              </>
            )}
            <Separator className="mobile-cta-separator" />
            <Footer>
              <OpenAccountButton
                data-testid={TestIDs.PREVIEW_PORTFOLIO.BUTTON}
                disabled={isLoading}
                onPress={handleOpenAccount}
              >
                {isLoading ? <Spinner height={20} width={20} white /> : 'Next'}
              </OpenAccountButton>
            </Footer>
          </Layout>
        </>
      )}
      <ConfirmationModal
        onRequestClose={() => setIsGeneralErrorModalOpen(false)}
        image={AlertBadge}
        actions={[
          {
            label: getRemoteString({
              key: 'error.generic.cta',
            }),
            type: ModalActionTypes.primary,
            handler: () => setIsGeneralErrorModalOpen(false),
          },
        ]}
        isOpen={isGeneralErrorModalOpen}
        message={getRemoteString({
          key: 'error.generic.body',
        })}
        title={getRemoteString({
          key: 'error.generic.title',
        })}
      />
    </>
  )
}

export default enhance(PreviewPortfolio)
